import { useEventListener, useMutationObserver, watchImmediate } from "@vueuse/core";
import { computed, onScopeDispose, ref, toValue, watch } from "vue";

import { MOUNT_READY_MARKER } from "#ce/framework/mount-ready";
import { getSlotContent } from "#ce/utils/slots";

import { activatorSlots, CONTROL_ATTRIBUTE, resolveActivatorControl } from "./activator-control";

const STATE_ATTRIBUTE = "aria-expanded";
const CLAIMED_ATTRIBUTES = ["role", "aria-haspopup", "aria-autocomplete", STATE_ATTRIBUTE];

// An `<input type="checkbox">` and a `<button>` are both focusable and neither takes typing, so
// the tag alone does not answer this.
const TEXT_INPUT_TYPES = ["text", "search", "url", "tel", "email", "password", ""];

/**
 * Whether typing into this control enters text it could autocomplete.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function acceptsText(element) {
  if (element.readOnly || element.disabled) {
    return false;
  }

  if (element.isContentEditable || element.tagName === "TEXTAREA") {
    return true;
  }

  return element.tagName === "INPUT" && TEXT_INPUT_TYPES.includes((element.type ?? "").toLowerCase());
}

// The roles that take typed text, neither of which supports `aria-expanded`.
const TEXTBOX_ROLES = ["textbox", "searchbox"];

/**
 * Whether this control resolves to a `textbox` or a `searchbox`, by the role its consumer wrote or
 * by the element it is. Every other explicit role is the consumer's own answer and is trusted.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isTextbox(element) {
  const declared = element.getAttribute("role");

  if (declared) {
    return TEXTBOX_ROLES.includes(declared.trim().toLowerCase());
  }

  if (element.isContentEditable || element.tagName === "TEXTAREA") {
    return true;
  }

  return element.tagName === "INPUT" && [...TEXT_INPUT_TYPES, "search"].includes((element.type ?? "").toLowerCase());
}

/**
 * Publishes the picker's popup state onto the control each assigned activator stands for, so the
 * node that takes focus is the one that says what it opens and whether it is open.
 *
 * {@link resolveActivatorControl} finds that control: an element that hands out its own, a
 * focusable element slotted directly, or the focusable child of a wrapper. Nothing opens a shadow
 * root it does not own.
 *
 * Whether a combobox is claimed at all follows `popupRole`: a panel exposing neither a listbox nor
 * a tree leaves its activator a plain control. Every attribute written here is restored to what the
 * markup carried when it is released.
 *
 * @param {object} options
 * @param {import('vue').Ref<HTMLSlotElement | null>} options.slot - The activator slot to read.
 * @param {import('vue').MaybeRefOrGetter<boolean>} options.expanded - Whether the popup is open.
 * @param {import('vue').MaybeRefOrGetter<string|undefined>} options.popupRole - the role the panel
 *   exposes, named by `aria-haspopup`. Undefined claims no combobox at all.
 * @param {import('vue').MaybeRefOrGetter<boolean>} options.enabled - False when there is no popup
 *   to describe, which releases every activator.
 */
export function useActivatorPopupState({ slot, expanded, popupRole, enabled }) {
  // What each claimed control's markup carried, so releasing one leaves it as it was found.
  const claimed = new Map();

  function restore(element, attribute) {
    const carried = claimed.get(element)?.[attribute] ?? null;

    if (carried === null) {
      element.removeAttribute(attribute);
    } else {
      element.setAttribute(attribute, carried);
    }
  }

  function release(element) {
    CLAIMED_ATTRIBUTES.forEach((attribute) => restore(element, attribute));
    claimed.delete(element);
  }

  // `observed` holds the assigned elements the mutation observer watches. A slot's assignment is
  // not reactive, so `slotchange` refreshes them.
  const observed = ref([]);
  // An activator may render a slot of its own, whose assignment changes without touching the DOM
  // this observes.
  const nestedSlots = ref([]);
  // The controls themselves. A wrapper that renders a slot holds controls assigned from outside its
  // own subtree, so what happens to them is not a mutation of anything above.
  const controlNodes = ref([]);

  // Flattened, so a slot showing its fallback content states that fallback. Empty while there is no
  // popup to describe, which releases every control and leaves the observer watching nothing.
  function handleSlotChange() {
    observed.value = toValue(enabled) ? getSlotContent(slot.value, { flatten: true }) : [];
    nestedSlots.value = observed.value.flatMap(activatorSlots);
    publish();
  }

  function sameNodes(left, right) {
    return left.length === right.length && left.every((node, index) => node === right[index]);
  }

  function publish() {
    const role = toValue(popupRole);
    const controls = observed.value.map(resolveActivatorControl).filter(Boolean);

    if (!sameNodes(controls, controlNodes.value)) {
      controlNodes.value = controls;
    }

    [...claimed.keys()].filter((element) => !controls.includes(element)).forEach(release);

    controls.forEach((element) => {
      if (!claimed.has(element)) {
        claimed.set(
          element,
          Object.fromEntries(CLAIMED_ATTRIBUTES.map((attribute) => [attribute, element.getAttribute(attribute)])),
        );
      }

      if (role) {
        element.setAttribute("role", "combobox");
        element.setAttribute("aria-haspopup", role);
        // Read from the control itself: a button takes no text and a readonly field takes none, so
        // both autocomplete nothing whatever the panel offers.
        element.setAttribute("aria-autocomplete", acceptsText(element) ? "list" : "none");
      } else {
        // Nothing to describe, so the consumer's own semantics stand.
        restore(element, "role");
        restore(element, "aria-haspopup");
        restore(element, "aria-autocomplete");
      }

      // `aria-expanded` belongs to a role that supports it, and `textbox` does not. Claiming the
      // combobox makes it supported; a field left as itself takes the state back rather than
      // carrying an attribute its role does not allow.
      if (role || !isTextbox(element)) {
        element.setAttribute(STATE_ATTRIBUTE, toValue(expanded) ? "true" : "false");
      } else {
        restore(element, STATE_ATTRIBUTE);
      }
    });
  }

  // The state changes on every open; the assignment changes only when the consumer swaps what it
  // slots, or when there stops being a popup to describe. Rescanning the slot is the heavier of the
  // two, so it follows the assignment rather than the state.
  watchImmediate([slot, () => toValue(enabled)], handleSlotChange, { flush: "post" });
  watch([() => toValue(expanded), () => toValue(popupRole)], publish, { flush: "post" });
  useEventListener(slot, "slotchange", handleSlotChange);
  useEventListener(nestedSlots, "slotchange", handleSlotChange);
  // What a nested slot assigns is neither the wrapper nor the control it resolves to: the marker an
  // element sets when it upgrades, and the tag it renders, both land on that host.
  const watchedNodes = computed(() => {
    const assigned = nestedSlots.value.flatMap((nested) => nested.assignedElements({ flatten: true }));

    return [...new Set([...observed.value, ...assigned, ...controlNodes.value])];
  });
  // A slot reports its own assignment and nothing about what happens inside it afterwards. Three
  // things do: a wrapper swapping the control it holds, an element upgrading late under the lazy
  // catalog build, and a control changing what it will accept.
  useMutationObserver(watchedNodes, publish, {
    childList: true,
    subtree: true,
    attributes: true,
    // Three groups, all changeable while the activator stays assigned. Which node is the control:
    // the attributes {@link resolveActivatorControl} reads, plus `tag`, standing in for the control
    // `sp-button` replaces inside a shadow root this observer cannot reach. What that control
    // accepts, which decides `aria-autocomplete` — `sp-select` switches `inputmode` after mount.
    // And whether it has upgraded, through `MOUNT_READY_MARKER`.
    attributeFilter: [
      MOUNT_READY_MARKER,
      CONTROL_ATTRIBUTE,
      "tabindex",
      "href",
      "contenteditable",
      "tag",
      "readonly",
      "disabled",
      "type",
      "inputmode",
    ],
  });
  function releaseEveryControl() {
    [...claimed.keys()].forEach(release);
  }

  onScopeDispose(releaseEveryControl);
}
